import React, { useEffect, useState } from 'react';
import JalaliDatePicker from '../../components/JalaliDatePicker';
import { openReport, downloadReport } from '../../components/report';
import { exportDownload, exportJson } from './exportsApi';
import SanamaDialog from './SanamaDialog';

/* ──────────────────────────────────────────────────────────────────────────
   پنجرهٔ چاپ ماژول تنظیم حساب

   گزارش‌های ثبت‌شده در backend/accounting/reports.py:
     • accounting.document_print  → روکش سند
     • accounting.document_list   → فهرست اسناد
     • accounting.daily_book      → دفتر روزنامه

   تب «ایجاد فایل»:
     سناما  → SanamaDialog
     سنا    → تاریخ شروع/پایان
     اکسل سمادنو (با/بدون تفکیک موضوع بدهی)
        → انتخاب «لیست بدهکاران / لیست بستانکاران / هر دو»
     اکسل آروین (فقط با محدوده سند)
   ────────────────────────────────────────────────────────────────────────── */

export default function AccountingPrintDialog({ year, selected, onClose }) {
  const [kind, setKind] = useState(selected ? 'doc' : 'list');   // doc | list | daily | files
  // گزینه‌های «جزییات سند»
  const [detail1, setD1] = useState(true);        // جزییات پروتکل ( بخش اول )
  const [detail2, setD2] = useState(false);       // جزییات پروتکل ( بخش دوم )
  const [sum, setSum] = useState(false);          // تجمیع حسابهای معین
  const [kol, setKol] = useState(false);          // فقط حسابهای کل
  const [printDate, setPrintDate] = useState(true);   // تاریخ چاپ سند
  const [havaleh, setHavaleh] = useState(true);       // حواله
  const [contractor, setContractor] = useState(false); // کارت پیمانکار
  const [statement, setStatement] = useState('');
  const [by, setBy] = useState('date');                          // date | no
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [fromNo, setFromNo] = useState('');
  const [toNo, setToNo] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [info, setInfo] = useState('');
  // تب ایجاد فایل
  const [fileKind, setFileKind] = useState('sanama');            // sanama | sana | samadno13 | samadno14 | arvin
  const [which, setWhich] = useState('both');                    // بدهکاران | بستانکاران | هر دو (سمادنو)
  const [sanaStart, setSanaStart] = useState('');
  const [sanaEnd, setSanaEnd] = useState('');
  const [sanamaTo, setSanamaTo] = useState(null);                // باز بودن پنجرهٔ سناما

  const listParams = () => (by === 'date'
    ? { year, by, from, to }
    : { year, by, from: fromNo, to: toNo });

  // دفتر روزنامه: بازه بر اساس تاریخ یا شماره سند؛ هر دو سر بازه اختیاری است
  const dailyParams = () => ({
    ...listParams(),
    doc_date: selected?.document_date || '',   // تاریخ امضاکنندگان در حالت شماره سند
  });

  const docParams = () => ({
    document_id: selected?.id || 0,
    year,
    detail1, detail2, sum, kol, contractor,
    print_report_date: printDate,
    print_havaleh: havaleh,
    statement,
  });

  // پیش‌فرض سنا: از ابتدای سال مالی تا امروز
  useEffect(() => {
    if (kind !== 'files' || fileKind !== 'sana' || sanaStart) return;
    exportJson('/sana/defaults/', { year })
      .then((d) => { setSanaStart(d.start); setSanaEnd(d.end); })
      .catch(() => {});
  }, [kind, fileKind, year, sanaStart]);

  const makeFile = async () => {
    setErr(''); setInfo(''); setBusy(true);
    try {
      if (fileKind === 'sanama') {
        // سناما فقط با بازهٔ تاریخ (یا شماره سند بدون «تا»)
        if (by !== 'date' && String(toNo).trim()) throw new Error('لطفا برای ايجاد فايل سناما از محدوده تاريخ استفاده کنيد');
        const t = by === 'date' ? to : '';
        await exportJson('/sanama/', { year, by: 'date', to: t, init: 1 });   // اعتبارسنجی سال/تاریخ
        setSanamaTo(t);
      } else if (fileKind === 'sana') {
        const name = await exportDownload('/sana/', { year, start: sanaStart, end: sanaEnd }, 'Sana.xls');
        setInfo(`فایل سنا ذخیره شد: ${name}`);
      } else if (fileKind === 'samadno13' || fileKind === 'samadno14') {
        const name = await exportDownload('/samadno/', {
          year, to: by === 'date' ? to : '', which, mode: fileKind === 'samadno13' ? 13 : 14,
        }, 'سمادنو.xlsx');
        setInfo(`فایل ایجاد شد: ${name}`);
      } else if (fileKind === 'arvin') {
        if (by === 'date') throw new Error('لطفا برای ايجاد فايل اکسل آروین از محدوده سند استفاده کنيد');
        const name = await exportDownload('/arvin/', { year, by: 'no', from: fromNo, to: toNo }, 'Arvin.zip');
        setInfo(`ایجاد فایل اکسل برای آروین خاتمه یافت: ${name}`);
      }
    } catch (e) {
      setErr(e.message || 'خطا در ایجاد فایل');
    } finally {
      setBusy(false);
    }
  };

  const run = async (fmt) => {
    setErr(''); setBusy(true);
    try {
      if (kind === 'doc') {
        if (!selected) throw new Error('ابتدا یک سند را انتخاب کنید');
        await openReport('accounting.document_print', docParams(), fmt);
      } else if (kind === 'daily') {
        await openReport('accounting.daily_book', dailyParams(), fmt);
      } else {
        await openReport('accounting.document_list', listParams(), fmt);
      }
      onClose();
    } catch (e) {
      setErr(e.message || 'خطا در تهیه گزارش');
    } finally {
      setBusy(false);
    }
  };

  const excel = async () => {
    setErr(''); setBusy(true);
    try {
      if (kind === 'daily') {
        await downloadReport('accounting.daily_book', dailyParams(), 'دفتر روزنامه.xlsx');
      } else {
        await downloadReport('accounting.document_list', listParams(), 'فهرست اسناد.xlsx');
      }
      onClose();
    } catch (e) {
      setErr(e.message || 'خطا در تهیه اکسل');
    } finally {
      setBusy(false);
    }
  };

  const chk = (label, value, set) => (
    <label style={p.chk}>
      <input type="checkbox" checked={value} onChange={(e) => set(e.target.checked)} />
      {label}
    </label>
  );

  return (
    <div style={p.overlay} onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={p.box}>
        <div style={p.head}>
          <span>🖨️ چاپ و گزارش — تنظیم حساب</span>
          <button onClick={onClose} style={p.x}>✕</button>
        </div>

        <div style={p.tabs}>
          <button onClick={() => setKind('doc')}
            style={{ ...p.tab, ...(kind === 'doc' ? p.tabOn : {}) }}>روکش سند</button>
          <button onClick={() => setKind('list')}
            style={{ ...p.tab, ...(kind === 'list' ? p.tabOn : {}) }}>فهرست اسناد</button>
          <button onClick={() => setKind('daily')}
            style={{ ...p.tab, ...(kind === 'daily' ? p.tabOn : {}) }}>دفتر روزنامه</button>
          <button onClick={() => setKind('files')}
            style={{ ...p.tab, ...(kind === 'files' ? p.tabOn : {}) }}>ایجاد فایل</button>
        </div>

        <div style={p.body}>
          {kind === 'doc' ? (
            <>
              <div style={p.row}>
                <b>سند انتخاب‌شده:</b>{' '}
                {selected
                  ? `شماره ${selected.document_no} — تاریخ ${selected.document_date}`
                  : <span style={{ color: '#b00' }}>سندی انتخاب نشده است</span>}
              </div>
              <div style={p.groupTitle}>جزییات سند</div>
              <div style={p.grid}>
                {chk('فقط حسابهای کل', kol, setKol)}
                {chk('تجمیع حسابهای معین', sum, setSum)}
                {chk('جزییات پروتکل ( بخش اول )', detail1, setD1)}
                {chk('جزییات پروتکل ( بخش دوم )', detail2, setD2)}
                {chk('تاریخ چاپ سند', printDate, setPrintDate)}
                {chk('حواله', havaleh, setHavaleh)}
              </div>
              <div style={p.grid}>
                {chk('کارت پیمانکار', contractor, setContractor)}
              </div>
              <div style={p.row}>
                <label style={p.lbl}>صورت وضعیت</label>
                <input value={statement} onChange={(e) => setStatement(e.target.value)} style={p.inp} />
              </div>
            </>
          ) : (
            <>
              <div style={p.grid}>
                <label style={p.chk}>
                  <input type="radio" checked={by === 'date'} onChange={() => setBy('date')} />
                  بر اساس تاریخ سند
                </label>
                <label style={p.chk}>
                  <input type="radio" checked={by === 'no'} onChange={() => setBy('no')} />
                  بر اساس شماره سند
                </label>
              </div>
              {by === 'date' ? (
                <div style={p.row}>
                  <label style={p.lbl}>از تاریخ</label>
                  <JalaliDatePicker value={from} onChange={setFrom} />
                  <label style={p.lbl}>تا تاریخ</label>
                  <JalaliDatePicker value={to} onChange={setTo} />
                </div>
              ) : (
                <div style={p.row}>
                  <label style={p.lbl}>از شماره</label>
                  <input value={fromNo} onChange={(e) => setFromNo(e.target.value)} style={p.inpNum} />
                  <label style={p.lbl}>تا شماره</label>
                  <input value={toNo} onChange={(e) => setToNo(e.target.value)} style={p.inpNum} />
                </div>
              )}
              <div style={p.hint}>
                سال مالی: {year}
                {kind === 'daily' && ' — اگر فقط «از» را بدهید تا آخر سال، اگر هیچ‌کدام را ندهید کل سال چاپ می‌شود'}
              </div>
              {kind === 'files' && (
                <>
                  <div style={p.groupTitle}>نوع فایل</div>
                  <div style={p.col}>
                    {[
                      ['sanama', 'ایجاد فایل سناما'],
                      ['sana', 'ایجاد فایل سنا'],
                      ['samadno13', 'ایجاد فایل اکسل سمادنو بدون تفکیک موضوع بدهی'],
                      ['samadno14', 'ایجاد فایل اکسل سمادنو'],
                      ['arvin', 'اکسل آروین'],
                    ].map(([v, l]) => (
                      <label key={v} style={p.chk}>
                        <input type="radio" checked={fileKind === v} onChange={() => setFileKind(v)} />
                        {l}
                      </label>
                    ))}
                  </div>
                  {fileKind === 'sana' && (
                    <div style={p.row}>
                      <label style={p.lbl}>تاریخ شروع</label>
                      <JalaliDatePicker value={sanaStart} onChange={setSanaStart} />
                      <label style={p.lbl}>تاریخ پایان</label>
                      <JalaliDatePicker value={sanaEnd} onChange={setSanaEnd} />
                    </div>
                  )}
                  {(fileKind === 'samadno13' || fileKind === 'samadno14') && (
                    <div style={p.grid}>
                      {[['debtors', 'لیست بدهکاران'], ['creditors', 'لیست بستانکاران'], ['both', 'هر دو']].map(([v, l]) => (
                        <label key={v} style={p.chk}>
                          <input type="radio" checked={which === v} onChange={() => setWhich(v)} />
                          {l}
                        </label>
                      ))}
                      <span style={p.hint}>تا تاریخ: {by === 'date' && to ? to : 'امروز'}</span>
                    </div>
                  )}
                  {fileKind === 'sanama' && (
                    <div style={p.hint}>برای سناما «بر اساس تاریخ سند» و تاریخ «تا» را بدهید (گردش از اول سال تا همان تاریخ).</div>
                  )}
                  {fileKind === 'arvin' && (
                    <div style={p.hint}>برای آروین «بر اساس شماره سند» را بدهید؛ برای هر سند یک فایل اکسل ساخته می‌شود.</div>
                  )}
                </>
              )}
            </>
          )}

          {err && <div style={p.err}>{err}</div>}
          {info && <div style={p.info}>{info}</div>}
        </div>

        <div style={p.foot}>
          {kind === 'files' ? (
            <button onClick={makeFile} disabled={busy} style={p.btnGo}>
              💾 ایجاد فایل
            </button>
          ) : (
            <>
              <button onClick={() => run('html')} disabled={busy} style={p.btnGo}>
                🖨️ نمایش و چاپ
              </button>
              <button onClick={() => run('pdf')} disabled={busy} style={p.btnPdf}>
                📄 PDF
              </button>
            </>
          )}
          {(kind === 'list' || kind === 'daily') && (
            <button onClick={excel} disabled={busy} style={p.btnXls}>
              📊 اکسل
            </button>
          )}
          <button onClick={onClose} style={p.btnCancel}>بستن</button>
        </div>
      </div>
      {sanamaTo !== null && (
        <SanamaDialog year={year} toDate={sanamaTo} onClose={() => setSanamaTo(null)} />
      )}
    </div>
  );
}

const p = {
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)', display: 'flex',
             alignItems: 'center', justifyContent: 'center', zIndex: 1300 },
  box: { background: '#fff', width: '620px', maxWidth: '95vw', borderRadius: '12px',
         overflow: 'hidden', boxShadow: '0 12px 40px rgba(0,0,0,.35)', direction: 'rtl' },
  head: { background: 'linear-gradient(135deg,#14233b,#1a2f50)', color: '#fff', padding: '10px 14px',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          fontWeight: 700, fontSize: '14px' },
  x: { background: 'transparent', border: 'none', color: '#fff', fontSize: '15px', cursor: 'pointer' },
  tabs: { display: 'flex', gap: '6px', padding: '8px 14px 0', background: '#f3f4f6' },
  tab: { padding: '7px 16px', border: 'none', borderRadius: '8px 8px 0 0', background: '#e2e5ea',
         fontFamily: 'inherit', fontSize: '13px', fontWeight: 700, cursor: 'pointer', color: '#334' },
  tabOn: { background: '#fff', color: '#14233b' },
  body: { padding: '14px', fontSize: '13px' },
  row: { display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '10px', flexWrap: 'wrap' },
  grid: { display: 'flex', gap: '14px', flexWrap: 'wrap', marginBottom: '10px' },
  groupTitle: { fontWeight: 700, color: '#14233b', margin: '2px 0 6px', fontSize: '12.5px' },
  chk: { display: 'flex', gap: '5px', alignItems: 'center', cursor: 'pointer' },
  lbl: { minWidth: '72px', color: '#334' },
  inp: { flex: 1, minWidth: '180px', padding: '6px 8px', border: '1px solid #cbd0d8',
         borderRadius: '6px', fontFamily: 'inherit', fontSize: '13px' },
  inpNum: { width: '110px', padding: '6px 8px', border: '1px solid #cbd0d8', borderRadius: '6px',
            fontFamily: 'inherit', fontSize: '13px', direction: 'ltr', textAlign: 'left' },
  hint: { color: '#667', fontSize: '12px' },
  info: { marginTop: '8px', padding: '7px 10px', background: '#e8f5e9', color: '#1b5e20',
          borderRadius: '6px', fontSize: '12.5px' },
  col: { display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '10px' },
  err: { marginTop: '8px', padding: '7px 10px', background: '#fdecea', color: '#a1201a',
         borderRadius: '6px', fontSize: '12.5px' },
  foot: { display: 'flex', gap: '8px', padding: '10px 14px', background: '#f3f4f6' },
  btnGo: { padding: '8px 18px', background: 'linear-gradient(135deg,#14233b,#1a2f50)', color: '#fff',
           border: 'none', borderRadius: '8px', fontFamily: 'inherit', fontSize: '13px',
           fontWeight: 700, cursor: 'pointer' },
  btnPdf: { padding: '8px 16px', background: 'linear-gradient(135deg,#7b1fa2,#9c27b0)', color: '#fff',
            border: 'none', borderRadius: '8px', fontFamily: 'inherit', fontSize: '13px',
            fontWeight: 700, cursor: 'pointer' },
  btnXls: { padding: '8px 16px', background: 'linear-gradient(135deg,#1b5e20,#2e7d32)', color: '#fff',
            border: 'none', borderRadius: '8px', fontFamily: 'inherit', fontSize: '13px',
            fontWeight: 700, cursor: 'pointer' },
  btnCancel: { padding: '8px 16px', background: '#e2e5ea', color: '#334', border: 'none',
               borderRadius: '8px', fontFamily: 'inherit', fontSize: '13px', fontWeight: 700,
               cursor: 'pointer', marginRight: 'auto' },
};
